import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import KPICard, { formatUSD } from "@/components/KPICard";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import { DASHBOARD } from "@/constants/testIds";
import { TrendingUp, TrendingDown } from "lucide-react";
import UpcomingEventsWidget from "@/components/UpcomingEventsWidget";
import PortfolioYieldCard from "@/components/PortfolioYieldCard";

const COUNTRY_COLORS = { AR: "#38BDF8", US: "#22C55E", MX: "#F59E0B" };
const CAT_COLORS = ["#22C55E", "#38BDF8", "#F59E0B", "#8B5CF6", "#EF4444", "#14B8A6", "#EAB308"];

function ChartWrapper({ title, children, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-border/60 bg-card p-5 sm:p-6 animate-fade-in-up">
      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-4">{title}</div>
      <div className="h-64 min-w-[240px]">{children}</div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [byCountry, setByCountry] = useState([]);
  const [byProperty, setByProperty] = useState([]);
  const [expCats, setExpCats] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, m, c, p, e] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/dashboard/monthly?months=12"),
          api.get("/dashboard/by_country"),
          api.get("/dashboard/by_property"),
          api.get("/dashboard/expense_categories"),
        ]);
        setSummary(s.data); setMonthly(m.data); setByCountry(c.data);
        setByProperty(p.data); setExpCats(e.data);
      } catch { /* handled by axios */ }
    })();
  }, []);

  if (!summary) {
    return <div className="text-muted-foreground text-sm">{t("common.loading")}</div>;
  }

  const netTone = summary.net_current_month >= 0 ? "positive" : "negative";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
            {t("nav.dashboard")}
          </h1>
          <div className="text-sm text-muted-foreground mt-1">
            Vista global — valores en USD con conversión automática
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{t("kpi.value")}</div>
          <div className="font-heading text-2xl sm:text-3xl font-semibold num">{formatUSD(summary.portfolio_value_usd)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <KPICard
          testId={DASHBOARD.kpiIncome}
          label={t("kpi.income")}
          value={formatUSD(summary.income_current_month)}
          current={summary.income_current_month}
          previous={summary.income_previous_month}
        />
        <KPICard
          testId={DASHBOARD.kpiExpense}
          label={t("kpi.expense")}
          value={formatUSD(summary.expense_current_month)}
          current={summary.expense_current_month}
          previous={summary.expense_previous_month}
          tone="neutral"
        />
        <KPICard
          testId={DASHBOARD.kpiNet}
          label={t("kpi.net")}
          value={formatUSD(summary.net_current_month)}
          current={summary.net_current_month}
          previous={summary.net_previous_month}
          tone={netTone}
        />
        <KPICard
          testId={DASHBOARD.kpiOccupancy}
          label={t("kpi.occupancy")}
          value={summary.occupancy_rate}
          suffix="%"
        />
      </div>

      <PortfolioYieldCard />

      <UpcomingEventsWidget days={15} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
        <div className="lg:col-span-2">
          <ChartWrapper title={t("chart.monthly")} testId={DASHBOARD.chartMonthly}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                <XAxis dataKey="period" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Legend />
                <Line type="monotone" dataKey="income" name="Ingresos" stroke="#22C55E" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="expense" name="Egresos" stroke="#EF4444" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="net" name="Neto" stroke="hsl(var(--foreground))" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartWrapper>
        </div>

        <ChartWrapper title={t("chart.categories")} testId={DASHBOARD.chartCategories}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={expCats} dataKey="total" nameKey="category" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {expCats.map((_, i) => (
                  <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
        <ChartWrapper title={t("chart.country")} testId={DASHBOARD.chartCountry}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byCountry}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
              <XAxis dataKey="country" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Legend />
              <Bar dataKey="income" name="Ingresos" fill="#22C55E" radius={[6, 6, 0, 0]} />
              <Bar dataKey="expense" name="Egresos" fill="#EF4444" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>

        <div className="lg:col-span-2 rounded-xl border border-border/60 bg-card p-5 sm:p-6 animate-fade-in-up flex flex-col" data-testid={DASHBOARD.propertyRanking}>
          <div className="flex items-baseline justify-between mb-4">
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{t("chart.ranking")}</div>
            <div className="text-[11px] text-muted-foreground num">{byProperty.length} {byProperty.length === 1 ? "propiedad" : "propiedades"}</div>
          </div>
          <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1 -mr-1">
            {byProperty.map((p) => {
              const net = p.net;
              const positive = net >= 0;
              return (
                <div key={p.property_id} className="flex items-center gap-3 border-b border-border/50 last:border-0 pb-3 last:pb-0">
                  <div className="w-10 h-10 rounded-md bg-secondary overflow-hidden shrink-0" style={{ backgroundImage: `url(${p.photo_url || ''})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{p.country} · {formatUSD(p.value_usd)}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-sm font-semibold num ${positive ? "text-emerald-500" : "text-rose-500"} inline-flex items-center gap-1`}>
                      {positive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                      {formatUSD(net)}
                    </div>
                    <div className="text-[11px] text-muted-foreground num">ROI {p.roi.toFixed(1)}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
